/**
 * React Query hook for the Agents tool.
 *
 *   useAgentRuns()  GET {API_BASE}/v1/cockpit-ops/agents
 *
 * Returns the list of agents (code, helpdesk), each with current
 * spend-today/cap, plus the 50 most recent workflow runs across all agent
 * workflows on the cockpit repo. Auto-refetches every 30 seconds (matching
 * the server cache).
 *
 * RBAC: same as cockpit-ops — viewer or admin Entra group, server-enforced.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from './api';
import { API_BASE } from './apiBase';

// The new API folds agent runs into the single GET /v1/cockpit-ops/agents
// endpoint (no separate /runs suffix).
export const AGENTS_API_BASE = `${API_BASE}/v1/cockpit-ops`;

export type AgentSlug = 'code' | 'helpdesk';

export type AgentRunStage = 'agent' | 'research' | 'fix' | 'qa' | 'unknown';

export type AgentRun = {
  id: number;
  repository: string;
  agent: AgentRunStage;
  workflow_name: string;
  status: string;
  conclusion: string | null;
  issue_number: number | null;
  issue_title: string | null;
  head_branch: string;
  started_at: string;
  updated_at: string;
  duration_seconds: number | null;
  html_url: string;
};

export type AgentDefinition = {
  slug: AgentSlug;
  name: string;
  description: string;
  status: 'live' | 'planned';
  spend_today_usd: number | null;
  daily_cap_usd: number | null;
};

export type AgentRunsResponse = {
  agents: AgentDefinition[];
  recent_runs: AgentRun[];
  fetched_at: string;
  stale: boolean;
};

export type UseAgentRunsResult = {
  agents: AgentDefinition[];
  recentRuns: AgentRun[];
  fetchedAt: string | null;
  stale: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  query: UseQueryResult<AgentRunsResponse, Error>;
};

export function useAgentRuns(): UseAgentRunsResult {
  const query = useQuery<AgentRunsResponse, Error>({
    queryKey: ['agents-runs'],
    queryFn: () => apiGet<AgentRunsResponse>(`${AGENTS_API_BASE}/agents`),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 25_000,
  });
  return {
    agents: query.data?.agents ?? [],
    recentRuns: query.data?.recent_runs ?? [],
    fetchedAt: query.data?.fetched_at ?? null,
    stale: query.data?.stale ?? false,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
    query,
  };
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export function statusLabel(run: AgentRun): { label: string; tone: 'ok' | 'warn' | 'err' | 'idle' } {
  if (run.status !== 'completed') return { label: 'running', tone: 'idle' };
  if (run.conclusion === 'success') return { label: 'success', tone: 'ok' };
  if (run.conclusion === 'failure') return { label: 'failed', tone: 'err' };
  if (run.conclusion === 'cancelled') return { label: 'cancelled', tone: 'warn' };
  return { label: run.conclusion ?? 'unknown', tone: 'idle' };
}
